import apiFetch from '@scale/frontend-lib/api-fetch';

import {
  GetTemplateResponse,
  ValidateTemplateRequest,
  ValidateTemplateResponse,
  PushTemplateRequest,
  PushTemplateResponse,
  GetAssetsResponse,
  GetAssetTasksRequest,
  GetAssetTasksResponse,
  AcknowledgeAssetRequest,
  UploadTasksResponse,
  RetryUploadTasksResponse,
  FetchInternalFieldsResponse,
  FetchInternalFieldsRequest,
  CreateTaskCategorizationRequest,
  CreateTaskCategorizationResponse,
  FetchTaskCategorizationResponse,
  FetchTaskCategorizationBatchMetricsRequest,
  FetchTaskCategorizationBatchMetricsResponse,
  PartialTemplate,
  GetAssetsChunkResponse,
  ParseWorkspaceContainersResponse,
  UpdateBenchmarkTemplateResponse,
  UpdateBenchmarkTemplateRequest,
  BenchmarkTemplate,
  FetchTaskTemplateParamsForVersionResponse,
} from '@scale/shared/taskTemplate/requestTypes';
import { useQuery } from 'react-query';
import { ChatTaskParams } from '@scale/shared/types/chat';

import { TaskType, ReviewLevel } from '@scale/shared-types';
import { TextCollectionField } from '@scale/shared/types/params/textcollection';
import { TaskCategorization } from '@scale/shared/taskTemplate/types';
import { IChatTaskFeatureConfig } from '@scale/shared/types/config';

export function validateTaskTemplate(
  taskType: TaskType,
  params: unknown,
): Promise<ValidateTemplateResponse> {
  return apiFetch<ValidateTemplateResponse, ValidateTemplateRequest>(
    null,
    `internal/task-template/validate`,
    {
      method: 'POST',
      body: {
        // TODO: serialize once instead of twice
        paramsJson: JSON.stringify(params),
        type: taskType,
      },
    },
  );
}

export const useProjectTaskTemplate = (projectId: string) => {
  return useQuery<GetTemplateResponse>({
    queryKey: ['projectTaskTemplate', projectId],
    queryFn: () => fetchTaskTemplate(projectId),
    staleTime: 0,
    cacheTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
};

export function fetchTaskTemplate(projectId: string) {
  return apiFetch<GetTemplateResponse>(null, `internal/task-template/${projectId}`);
}

export const useLatestApprovedTemplate = (projectId: string) => {
  return useQuery<GetTemplateResponse>({
    queryKey: ['latestApprovedTemplate', projectId],
    queryFn: () => fetchLatestApprovedTemplate(projectId),
  });
};

export const fetchLatestApprovedTemplate = async (projectId: string) => {
  return await apiFetch<GetTemplateResponse>(
    null,
    `internal/task-template/latest-approved/${projectId}`,
  );
};

export async function fetchNumUploadedAssetsChunks(projectId: string) {
  return await apiFetch<GetAssetsChunkResponse>(
    null,
    `internal/task-template/${projectId}/num-asset-chunks`,
  );
}

export async function fetchUploadedAssetsChunk(projectId: string, chunk: number) {
  return await apiFetch<GetAssetsResponse>(
    null,
    `internal/task-template/${projectId}/assets/${chunk}`,
  );
}

export function fetchAssetTasks(projectId: string, assetId: string, uniqueIds: string[]) {
  return apiFetch<GetAssetTasksResponse, GetAssetTasksRequest>(
    null,
    `internal/task-template/${projectId}/assets-tasks`,
    {
      method: 'POST',
      body: {
        assetId,
        uniqueIds,
      },
    },
  );
}

export function uploadTasks(
  projectId: string,
  csvData: Record<string, string>[],
  batchId?: string,
  skipAttachmentProcessing?: boolean,
  uniqueIdColumns?: string[],
) {
  return apiFetch<UploadTasksResponse>(null, `internal/task-template/${projectId}/upload`, {
    method: 'POST',
    body: { csvData, batchId, skipAttachmentProcessing, uniqueIdColumns },
  });
}

export function retryUploadTasks(projectId: string, assetId: string) {
  return apiFetch<RetryUploadTasksResponse>(
    null,
    `internal/task-template/${projectId}/retry-upload`,
    {
      method: 'POST',
      body: { assetId },
    },
  );
}

export function publishAcknowledgedFailure(
  projectId: string,
  assetId: string,
  acknowledgedFailure: boolean,
) {
  return apiFetch<{}, AcknowledgeAssetRequest>(
    null,
    `internal/task-template/${projectId}/acknowledge-asset-failure`,
    {
      method: 'POST',
      body: {
        assetId,
        acknowledgedFailure,
      },
    },
  );
}

export function pushTaskTemplate(
  projectId: string,
  partialTemplate: PartialTemplate,
  skipJsonFormatMode: boolean = false,
): Promise<PushTemplateResponse> {
  return apiFetch<PushTemplateResponse, PushTemplateRequest>(
    null,
    `internal/task-template/${projectId}`,
    {
      method: 'POST',
      body: {
        // TODO: serialize once instead of twice
        partialTemplateJson: JSON.stringify(partialTemplate),
        skipJsonFormatMode,
      },
      maxRetries: 3,
      passError: true,
    },
  );
}

export function fetchInternalFields(
  projectId: string,
  fields: TextCollectionField[],
): Promise<FetchInternalFieldsResponse> {
  return apiFetch<FetchInternalFieldsResponse, FetchInternalFieldsRequest>(
    null,
    `internal/task-template/${projectId}/fetch-fields-with-internal-fields`,
    {
      method: 'POST',
      body: {
        fields,
      },
    },
  );
}

export function createCategorizationMetadata(
  projectId: string,
  taskCategorization: TaskCategorization,
): Promise<CreateTaskCategorizationResponse> {
  return apiFetch<CreateTaskCategorizationResponse, CreateTaskCategorizationRequest>(
    null,
    `internal/task-template/${projectId}/task-categorization-metadata`,
    {
      method: 'POST',
      body: {
        taskCategorization,
      },
    },
  );
}

export function fetchCategorizationMetadata(projectId: string) {
  return apiFetch<FetchTaskCategorizationResponse>(
    null,
    `internal/task-template/${projectId}/task-categorization-metadata`,
  );
}

export function fetchTaskCategorizationBatchMetrics(
  projectId: string,
  batchId: string,
): Promise<FetchTaskCategorizationBatchMetricsResponse> {
  return apiFetch<
    FetchTaskCategorizationBatchMetricsResponse,
    FetchTaskCategorizationBatchMetricsRequest
  >(null, `internal/task-template/${projectId}/task-categorization-batch-metrics`, {
    method: 'POST',
    body: {
      batchId,
    },
  });
}

export function fetchChatTaskFeatureConfig(
  projectId: string,
): Promise<{ chatTaskFeatureConfig: IChatTaskFeatureConfig }> {
  return apiFetch(null, `internal/task-template/chat-task-feature-config/${projectId}`);
}

export function updateChatTaskFeatureConfig(
  projectId: string,
  chatTaskFeatureConfig: IChatTaskFeatureConfig,
): Promise<{ success: boolean }> {
  return apiFetch(null, `internal/task-template/chat-task-feature-config/${projectId}`, {
    method: 'POST',
    body: {
      chatTaskFeatureConfig,
    },
    maxRetries: 3,
  });
}

export function fetchTaskTemplateForVersion(
  projectId: string,
  templateVersion: number,
): Promise<FetchTaskTemplateParamsForVersionResponse> {
  return apiFetch<FetchTaskTemplateParamsForVersionResponse>(
    null,
    `internal/task-template/${projectId}/version/${templateVersion}`,
  );
}

export function parseWorkspaceContainers(
  projectId: string,
): Promise<ParseWorkspaceContainersResponse> {
  return apiFetch(null, `internal/task-template/${projectId}/parse-workspace-containers`, {
    method: 'GET',
  });
}

export function updateBenchmarkTemplate(
  projectId: string,
  reviewLevel: ReviewLevel,
  benchmarkTemplate: BenchmarkTemplate,
): Promise<UpdateBenchmarkTemplateResponse> {
  return apiFetch<UpdateBenchmarkTemplateResponse, UpdateBenchmarkTemplateRequest>(
    null,
    `internal/task-template/${projectId}/update-benchmark-template`,
    {
      method: 'POST',
      body: {
        reviewLevel,
        benchmarkTemplate,
      },
    },
  );
}

export function fetchPrefilledResponses(taskType: TaskType, params: ChatTaskParams) {
  return apiFetch(null, `internal/task-template/prefilled-responses`, {
    method: 'POST',
    body: { taskType, params },
  });
}
